import $ from 'jquery';
import { generateIconElement, ICON } from "../../Icons/Icons";

/**
 * PlanUpdateEstimationModal - A component for showing estimation when upgrading or downgrading subscription plans
 */
class PlanUpdateEstimationModal {
  /**
   * Get the modal template
   * @returns {string} HTML template
   */
  modalTemplate() {
    return `
<div class="confirm-modal confirm-modal--light-theme middle plan-update-estimation-modal">
  <div class="confirm-dialog">
    ${generateIconElement(ICON.CLOSE, { classes: 'confirm-close' }).prop('outerHTML')}
    
    <div class="plan-update-estimation-modal__title">
      <span id="modal_title_upgrade" data-lang="plan-update-upgrade-confirmation-title" style="display: none;">
        ${$trans('plan-update-upgrade-confirmation-title')}
      </span>
      <span id="modal_title_downgrade" data-lang="plan-update-downgrade-confirmation-title" style="display: none;">
        ${$trans('plan-update-downgrade-confirmation-title')}
      </span>
    </div>
    
    <div class="plan-update-estimation-modal__content">
      <table class="plan-update-estimation-modal__table">
        <thead>
          <tr>
            <th data-lang="plan-update-items-text">${$trans('plan-update-items-text')}</th>
            <th data-lang="plan-update-period-text">${$trans('plan-update-period-text')}</th>
            <th data-lang="plan-update-qty-text" class="has-text-right">${$trans('plan-update-qty-text')}</th>
            <th data-lang="plan-update-amount-text" class="has-text-right text-nowrap">${$trans('plan-update-amount-text')}</th>
          </tr>
        </thead>
        <tbody id="estimation_table_body">
          <!-- Content will be dynamically generated -->
        </tbody>
      </table>
      
      <div class="plan-update-estimation-modal__notes">
        <p class="plan-update-estimation-modal__note plan-update-estimation-modal__note--estimation" data-lang="plan-update-estimation-note">
          ${$trans('plan-update-estimation-note')}
        </p>
        <p class="plan-update-estimation-modal__note plan-update-estimation-modal__note--payment" data-lang="plan-update-payment-note">
          ${$trans('plan-update-payment-note')}
        </p>
        <p class="plan-update-estimation-modal__note plan-update-estimation-modal__note--credit" data-lang="plan-update-credit-note" style="display: none;">
          ${$trans('plan-update-credit-note')}
        </p>
      </div>
    </div>
    
    <div class="confirm-footer my-5">
      <button id="cancel_plan_update" class="buttn secondary" data-lang="cancel-text">
        ${$trans('cancel-text')}
      </button>
      
      <button id="confirm_plan_update" class="buttn primary">
        ${$trans('plan-update-confirm-upgrade-text')}
      </button>
    </div>
  </div>
</div>
    `;
  }

  /**
   * Constructor
   */
  constructor() {
    this.checkoutUrl = '/billing/checkout';
    this.newPlanData = null;
    this.mode = null;

    this.render();
    this.addEventListeners();
  }

  /**
   * Render the modal
   */
  render() {
    this.$modal = $(this.modalTemplate());
    $('body').append(this.$modal);

    this.$elements = {
      $cancelButton: this.$modal.find('#cancel_plan_update'),
      $confirmButton: this.$modal.find('#confirm_plan_update'),
      $close: this.$modal.find('.confirm-close'),
      $tableBody: this.$modal.find('#estimation_table_body'),
      $estimationNote: this.$modal.find('.plan-update-estimation-modal__note--estimation'),
      $paymentNote: this.$modal.find('.plan-update-estimation-modal__note--payment'),
      $creditNote: this.$modal.find('.plan-update-estimation-modal__note--credit'),
      $modalTitleUpgrade: this.$modal.find('#modal_title_upgrade'),
      $modalTitleDowngrade: this.$modal.find('#modal_title_downgrade')
    };
  }

  /**
   * Add event listeners
   */
  addEventListeners() {
    // Cancel button - hide modal
    this.$elements.$cancelButton.on('click', () => {
      this.hide();
    });

    // Close button - hide modal
    this.$elements.$close.on('click', () => {
      this.hide();
    });

    // Confirm button - submit plan update
    this.$elements.$confirmButton.on('click', () => {
      // Disable button to prevent duplicate requests
      this.$elements.$confirmButton.prop('disabled', true).addClass('disabled');
      this.confirmPlanUpdate();
    });

    // Click outside modal to close
    this.$modal.on('click', (e) => {
      if (e.target === this.$modal[0]) {
        this.hide();
      }
    });
  }

  /**
   * Show the modal with plan update estimation
   * @param {string} planCode - Code of the new plan
   * @param {string} interval - Billing interval (month/year)
   * @param {string} mode - Mode of update (upgrade/downgrade)
   */
  show(planCode, interval, mode) {
    if (!planCode || !interval || !mode) {
      console.error('Missing required parameters for PlanUpdateEstimationModal');
      return;
    }

    this.mode = mode;

    // Update modal title based on mode
    const $titleUpgrade = this.$modal.find('#modal_title_upgrade');
    const $titleDowngrade = this.$modal.find('#modal_title_downgrade');

    if (mode === 'upgrade') {
      $titleUpgrade.show();
      $titleDowngrade.hide();
    } else {
      $titleUpgrade.hide();
      $titleDowngrade.show();
    }

    // Update confirm button text based on mode
    if (mode === 'upgrade') {
      this.$elements.$confirmButton.attr('data-lang', 'plan-update-confirm-upgrade-text').text($trans('plan-update-confirm-upgrade-text'));
      this.$elements.$paymentNote.show();
      this.$elements.$creditNote.hide();
    } else {
      this.$elements.$confirmButton.attr('data-lang', 'plan-update-confirm-downgrade-text').text($trans('plan-update-confirm-downgrade-text'));
      this.$elements.$paymentNote.hide();
      this.$elements.$creditNote.show();
    }

    // Get subscription plan data and calculate estimation
    this.getSubscriptionData(planCode, interval);

    this.$modal.addClass('visible').show();
  }

  /**
   * Get subscription data and calculate estimation
   * @param {string} planCode - Code of the new plan
   * @param {string} interval - Billing interval (month/year)
   */
  getSubscriptionData(planCode, interval) {
    // Use window.subscriptionPlan and window.stripePlans instead of fetching from JSON
    if (!window.subscriptionPlan) {
      console.error('No subscription plan data available');
      return;
    }

    // Find price for the new plan from stripePlans
    if (!window.stripePlans || !window.stripePlans[planCode] || !window.stripePlans[planCode][interval]) {
      console.error(`No price data available for plan ${planCode} with interval ${interval}`);
      return;
    }

    this.newPlanData = {
      plan: {
        code: planCode,
        name: window.stripePlans[planCode][interval].name
      },
      price: window.stripePlans[planCode][interval]
    };

    this.calculateAndRenderEstimation();
  }

  /**
   * Calculate and render estimation
   */
  calculateAndRenderEstimation() {
    const currentStatus = window.subscriptionPlan.status;
    const trial_end = window.subscriptionPlan.trial_end;
    const trial_start = window.subscriptionPlan.trial_start;
    const isInTrial = currentStatus === 'trialing' && trial_end && trial_end > Math.floor(Date.now() / 1000);

    const currentTime = Math.floor(Date.now() / 1000);
    const currentPeriodEnd = window.subscriptionPlan.current_period_end;
    const currentPlanAmount = window.subscriptionPlan.price.unit_amount;
    const currentPlanCurrency = window.subscriptionPlan.price.currency;

    const currencySymbol = window.subscriptionPlan.price.currency_symbol || this.newPlanData.price.currency_symbol;

    const newPlanAmount = this.newPlanData.price.unit_amount;
    const balance = Math.abs(window.subscriptionPlan.customer?.balance || 0) / 100; // Convert from cents to currency unit

    // Calculate unused time credit - only for active paid subscriptions
    let unusedTimeCredit = 0;

    // Only calculate unused time credit for subscriptions with appropriate status
    // Valid statuses for unused time calculation: active, past_due, canceled (but still in active period)
    const validStatusesForCredit = ['active', 'past_due', 'canceled'];

    if (
      currentPeriodEnd &&
      validStatusesForCredit.includes(currentStatus) &&
      currentStatus !== null && // Not a free plan
      currentPlanAmount > 0 // Not a free plan with amount 0
    ) {
      const remainingSeconds = currentPeriodEnd - currentTime;
      const totalPeriodSeconds = this.calculateTotalPeriodSeconds(window.subscriptionPlan.price.interval);
      // Ensure unused time credit is not negative and not greater than the original plan amount
      const unusedTimeCreditRaw = (remainingSeconds / totalPeriodSeconds) * currentPlanAmount;
      unusedTimeCredit = Math.min(Math.max(Math.round(unusedTimeCreditRaw * 100) / 100, 0), currentPlanAmount);
    }

    // Calculate total due or credit
    let totalDue = isInTrial ? 0 : newPlanAmount - unusedTimeCredit;

    // Handle balance differently based on mode
    let appliedBalance = 0;
    if (this.mode === 'upgrade' && balance > 0 && !isInTrial) {
      // For upgrades, only apply balance up to the total due amount
      appliedBalance = Math.min(balance, totalDue);
      totalDue -= appliedBalance;
    }

    // Ensure total due is not negative for upgrades and not greater than the new plan amount
    if (this.mode === 'upgrade') {
      totalDue = Math.max(0, totalDue);
    } else if (this.mode === 'downgrade' && !isInTrial) {
      // For downgrades, ensure credit is not greater than the original plan amount
      const creditAmount = Math.abs(totalDue);
      if (creditAmount > currentPlanAmount) {
        totalDue = -currentPlanAmount;
      }
    }

    // Build table rows
    let tableHtml = '';

    // New plan row - calculate end date based on new plan interval
    const periodStartDate = new Date();
    let periodEndDate;

    // If in trial, use trial end date
    if (isInTrial) {
      periodEndDate = new Date(trial_end * 1000);
    } else {
      // Calculate end date based on new plan interval
      if (this.newPlanData.price.interval === 'year') {
        periodEndDate = new Date(periodStartDate);
        periodEndDate.setFullYear(periodEndDate.getFullYear() + this.newPlanData.price.interval_count);
      } else if (this.newPlanData.price.interval === 'month') {
        periodEndDate = new Date(periodStartDate);
        periodEndDate.setMonth(periodEndDate.getMonth() + this.newPlanData.price.interval_count);
      } else {
        // Default to 30 days if interval is unknown
        periodEndDate = new Date(periodStartDate);
        periodEndDate.setDate(periodEndDate.getDate() + 30);
      }
    }

    const periodText = `${this.formatDate(periodStartDate)} – ${this.formatDate(periodEndDate)}`;

    // For trial subscriptions, show special trial row
    if (isInTrial) {
      tableHtml += `
      <tr class="plan-update-estimation-modal__row plan-update-estimation-modal__row--trial">
        <td><strong data-lang="plan-update-trial-period-text">${$trans('plan-update-trial-period-text')}</strong> <strong>${this.newPlanData.plan.name}</strong></td>
        <td>${this.formatDate(new Date(trial_start * 1000))} – ${this.formatDate(new Date(trial_end * 1000))}</td>
        <td class="has-text-right">1</td>
        <td class="has-text-right text-nowrap">${this.formatCurrency(0, currentPlanCurrency, currencySymbol)}</td>
      </tr>
      `;
    } else {
      // Regular new plan row
      tableHtml += `
      <tr class="plan-update-estimation-modal__row plan-update-estimation-modal__row--new-plan">
        <td><strong>${this.newPlanData.plan.name} (${$trans('plan-update-new-plan-text')})</strong></td>
        <td>${periodText}</td>
        <td class="has-text-right">1</td>
        <td class="has-text-right text-nowrap">${this.formatCurrency(newPlanAmount, currentPlanCurrency, currencySymbol)}</td>
      </tr>
      `;
    }

    // Unused time credit row - only show if not in trial
    if (unusedTimeCredit && !isInTrial) {
      tableHtml += `
      <tr class="plan-update-estimation-modal__row plan-update-estimation-modal__row--unused-time">
        <td>${$trans('plan-update-unused-time-text')} <em>${window.subscriptionPlan.plan.name}</em> ${$trans('plan-update-after-text')} ${this.formatDate(periodStartDate)}</td>
        <td>–</td>
        <td class="has-text-right">1</td>
        <td class="has-text-right text-nowrap plan-update-estimation-modal__amount--negative">–${this.formatCurrency(unusedTimeCredit, currentPlanCurrency, currencySymbol)}</td>
      </tr>
      `;
    }

    // Applied balance row (if applicable) - only for upgrades and not in trial
    if (this.mode === 'upgrade' && appliedBalance > 0 && !isInTrial) {
      tableHtml += `
        <tr class="plan-update-estimation-modal__row plan-update-estimation-modal__row--balance">
          <td>${$trans('plan-update-applied-balance-text')}</td>
          <td>–</td>
          <td class="has-text-right">–</td>
          <td class="has-text-right text-nowrap plan-update-estimation-modal__amount--negative">–${this.formatCurrency(appliedBalance, currentPlanCurrency, currencySymbol)}</td>
        </tr>
      `;
    }

    // Total due row - only show if not in trial or if there's an amount due
    if ((this.mode === 'upgrade' || totalDue > 0) && (!isInTrial || totalDue > 0)) {
      tableHtml += `
        <tr class="plan-update-estimation-modal__row plan-update-estimation-modal__row--total">
          <td><strong>${$trans('plan-update-estimated-total-due-text')}</strong></td>
          <td>–</td>
          <td class="has-text-right">–</td>
          <td class="has-text-right text-nowrap plan-update-estimation-modal__amount--total"><strong>${this.formatCurrency(Math.max(0, totalDue), currentPlanCurrency, currencySymbol)}</strong></td>
        </tr>
      `;
    }

    // Credit row (for downgrade) - only if not in trial
    if (this.mode === 'downgrade' && totalDue < 0 && !isInTrial) {
      const creditAmount = Math.abs(totalDue);
      tableHtml += `
        <tr class="plan-update-estimation-modal__row plan-update-estimation-modal__row--total">
          <td><strong>${$trans('plan-update-estimated-total-due-text')}</strong></td>
          <td>–</td>
          <td class="has-text-right">–</td>
          <td class="has-text-right text-nowrap plan-update-estimation-modal__amount--total"><strong>${this.formatCurrency(0, currentPlanCurrency, currencySymbol)}</strong></td>
        </tr>
        <tr class="plan-update-estimation-modal__row plan-update-estimation-modal__row--credit">
          <td><strong>${$trans('plan-update-estimated-credit-text')}</strong></td>
          <td>–</td>
          <td class="has-text-right">–</td>
          <td class="has-text-right text-nowrap plan-update-estimation-modal__amount--credit"><strong>–${this.formatCurrency(creditAmount, currentPlanCurrency, currencySymbol)}</strong></td>
        </tr>
      `;
    }

    // Update table content
    this.$elements.$tableBody.html(tableHtml);
  }

  /**
   * Calculate total period seconds based on interval
   * @param {string} interval - Billing interval (month/year)
   * @returns {number} Total seconds in the period
   */
  calculateTotalPeriodSeconds(interval) {
    const secondsInDay = 86400;

    switch (interval) {
      case 'year':
        return secondsInDay * 365;
      case 'month':
        return secondsInDay * 30;
      case 'week':
        return secondsInDay * 7;
      default:
        return secondsInDay * 30; // Default to month
    }
  }

  /**
   * Confirm plan update
   */
  confirmPlanUpdate() {
    // In a real implementation, you would submit the form to the server
    // For now, we'll just redirect to the subscription page
    window.location.href = `${this.checkoutUrl}?priceId=${this.newPlanData.price.id}`;
  }

  /**
   * Format currency
   * @param {number} amount - Amount in euros (not cents)
   * @param {string} currency - Currency code
   * @param {string} symbol - Currency symbol
   * @returns {string} Formatted currency
   */
  formatCurrency = function(amount, currency, symbol = '€') {
    return `${symbol}${amount.toFixed(2)}`;
  }

  /**
   * Format date
   * @param {Date} date - Date object
   * @returns {string} Formatted date
   */
  formatDate = function(date) {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  }

  /**
   * Hide the modal
   */
  hide() {
    this.$modal.removeClass('visible').hide();
  }

  /**
   * Destroy the modal and remove from DOM
   */
  destroy() {
    if (this.$modal) {
      this.$modal.remove();
    }
  }
}

export default PlanUpdateEstimationModal;
